/**
 * 牌桌 —— 四家布局 + 舍牌河 + 中央信息
 *
 * 布局（以自家为下方）：
 *
 *            ┌─── 对家（上）───┐
 *            │  手牌/副露/河   │
 *   上家(左) │                 │ 下家(右)
 *   手牌竖排 │    中央信息      │ 手牌竖排
 *            │  (场风/剩余/宝牌)│
 *            └─── 自家（下）───┘
 *              手牌/副露/河
 */

import { memo, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Tile, MeldDisplay, type TileScale } from './Tile.js';
import { tileUrlByIndex } from '../tiles.js';

/** 玩家视角数据 */
export interface PlayerView {
  readonly seat: number;
  readonly hand: readonly number[]; // 牌索引数组
  readonly handRed: readonly boolean[]; // 哪些是赤宝
  readonly drawnTile: number | null; // 刚摸的牌（兼容旧字段）
  /** 刚摸的牌在 hand 数组中的下标（-1 = 无）；用于高亮 */
  readonly drawnIndex: number;
  readonly melds: ReadonlyArray<{ kind: string; start: number; open: boolean; calledIndex?: number }>;
  readonly discards: readonly number[];
  readonly discardsRed: readonly boolean[];
  readonly riichi: boolean;
  readonly riichiDiscardIndex: number | null; // 立直宣言牌在舍牌中的位置
  readonly score: number;
  readonly isDealer: boolean;
  readonly wind: 'east' | 'south' | 'west' | 'north';
  readonly isTenpai?: boolean;
}

/** 中央信息 */
export interface TableCenterInfo {
  readonly roundWind: 'east' | 'south' | 'west' | 'north';
  readonly roundNumber: number;
  readonly honba: number;
  readonly riichiSticks: number;
  readonly tilesRemaining: number;
  readonly doraIndicators: readonly number[];
  readonly activeSeat: number;
}

// ════════════════════════════════════════════════════════════════
// 常量
// ════════════════════════════════════════════════════════════════

const WIND_NAMES: Record<string, string> = {
  east: '东',
  south: '南',
  west: '西',
  north: '北',
};

const SEAT_LABELS = ['自家', '下家', '对家', '上家'];

/** 舍牌河每行张数（上/下两家：横向河，宽度受限）*/
const RIVER_COLS = 6;

/**
 * 竖屏时上家/下家（左/右）舍牌河的每行张数。
 *
 * 左右两家的河是「竖条」：加列数只让它变高（竖屏纵向空间充裕），
 * 横向宽度 = 行数 × 牌高 → 行数减少 → 竖条更窄，不会挤到贴边。
 * ⚠️ 踩过：竖屏按 6 列排，一局后期行数 ≥3，横向撑爆中间列。
 */
const RIVER_COLS_SIDE_PORTRAIT = 10;

/** 各家舍牌河旋转方向（顺时针度数）——对应该家在桌上的朝向 */
export type RiverRotation = 0 | 90 | 180 | 270;

/** 牌宽对应的 CSS 变量（与 Tile.tsx 的 SCALE_VAR 保持一致，用于估算旋转后外框）*/
const TILE_VAR: Record<TileScale, string> = {
  xs: 'var(--tile-other)',
  sm: 'var(--tile-meld)',
  md: 'var(--tile-hand)',
  lg: 'var(--tile-hand)',
};

// ════════════════════════════════════════════════════════════════
// 舍牌河
//
// 设计：始终按「自家视角」渲染——横向、牌正立、从左到右 n 张；
//       再整体旋转 rotate 度。
//   → 对家 rotate=180（牌朝对家、读序左右翻转）
//   → 上家 rotate=90 / 下家 rotate=270（牌朝该家、竖排）
//   → 四家在各自视角下完全一致（朝向自己 + 从左到右）
// ════════════════════════════════════════════════════════════════

const DiscardRiver = memo(function DiscardRiver({
  discards,
  discardsRed,
  riichiIndex,
  scale,
  rotate = 0,
  cols: colsProp = RIVER_COLS,
}: {
  discards: readonly number[];
  discardsRed: readonly boolean[];
  riichiIndex: number | null;
  scale: TileScale;
  rotate?: RiverRotation;
  /** 每行张数（默认 RIVER_COLS；左右两家竖屏下更大）*/
  cols?: number;
}) {
  const tw = TILE_VAR[scale]; // CSS 长度表达式（响应式牌宽）
  const th = `calc(${tw} * 4 / 3)`;
  const gap = 1; // 与 .river gap 一致
  const pad = 2; // 与 .river padding 一致

  if (discards.length === 0) {
    return <div className="river-slot" style={{ width: tw, height: th }} />;
  }

  const n = discards.length;
  const cols = Math.min(colsProp, n);
  const rows = Math.ceil(n / colsProp);
  // 用 calc() 保持对 CSS 变量（响应式牌宽）的依赖
  const baseW = `calc(${cols} * ${tw} + ${(cols - 1) * gap + pad * 2}px)`;
  const baseH = `calc(${rows} * ${th} + ${(rows - 1) * gap + pad * 2}px)`;
  const swap = rotate === 90 || rotate === 270;
  const slotW = swap ? baseH : baseW;
  const slotH = swap ? baseW : baseH;

  return (
    <div className="river-slot" style={{ width: slotW, height: slotH }}>
      <div
        className="river"
        style={
          {
            width: baseW,
            height: baseH,
            '--river-cols': String(colsProp),
            transform: rotate ? `rotate(${rotate}deg)` : undefined,
          } as CSSProperties
        }
      >
        {discards.map((idx, i) => (
          <div key={i} className="river-tile">
            <Tile
              index={idx}
              isRed={discardsRed[i] ?? false}
              scale={scale}
              // 立直宣言牌：在本行内横置
              orientation={riichiIndex === i ? 'rotate-right' : 'normal'}
              highlighted={riichiIndex === i}
            />
          </div>
        ))}
      </div>
    </div>
  );
});

// ════════════════════════════════════════════════════════════════
// 玩家面板
// ════════════════════════════════════════════════════════════════

const PlayerPanel = memo(function PlayerPanel({
  player,
  isSelf,
  isActive,
}: {
  player: PlayerView;
  isSelf: boolean;
  isActive: boolean;
}) {
  const windLabel = WIND_NAMES[player.wind] ?? '?';

  return (
    <div className={`player-panel ${isSelf ? 'player-self' : 'player-other'} ${isActive ? 'player-active' : ''}`}>
      <div className="player-info">
        <span className={`wind-badge wind-${player.wind}`}>{windLabel}</span>
        {player.isDealer && <span className="dealer-badge">亲</span>}
        <span className="player-name">{SEAT_LABELS[player.seat]}</span>
        <span className="player-score">{player.score.toLocaleString()}</span>
        {player.riichi && <span className="riichi-badge">立直</span>}
        {isSelf && player.isTenpai && <span className="tenpai-badge">听牌</span>}
      </div>
    </div>
  );
});

/**
 * 中央正方形内侧四边上的玩家信息条。
 * 「从各家的视角看都在正方形底边」：
 *   bottom 边 = 自家(0°)、top 边 = 对家(180°)、left 边 = 上家(90°)、right 边 = 下家(270°)。
 */
const CenterPanel = memo(function CenterPanel({
  player,
  isSelf,
  isActive,
  edge,
}: {
  player: PlayerView;
  isSelf: boolean;
  isActive: boolean;
  edge: 'top' | 'bottom' | 'left' | 'right';
}) {
  return (
    <div className={`center-panel center-panel-${edge}`}>
      <div className="center-panel-inner">
        <PlayerPanel player={player} isSelf={isSelf} isActive={isActive} />
        {/* 第二行：该家立直时显示横放的 1000 点棒（常驻占位，保证布局稳定） */}
        <div className="panel-stick-row">
          {player.riichi && <span className="panel-stick" />}
        </div>
      </div>
    </div>
  );
});

/**
 * 中央方块四角的玩家头像（参考传统牌桌：四角各一个头像、四边是信息条）。
 * 头像内的风位按各家视角旋转，与信息条一致：自家 0°／对家 180°／上家 90°／下家 270°。
 */
const CenterAvatar = memo(function CenterAvatar({
  player,
  isSelf,
  isActive,
  corner,
}: {
  player: PlayerView;
  isSelf: boolean;
  isActive: boolean;
  corner: 'tl' | 'tr' | 'bl' | 'br';
}) {
  return (
    <div
      className={`center-avatar center-avatar-${corner} avatar-wind-${player.wind} ${
        isActive ? 'avatar-active' : ''
      } ${isSelf ? 'avatar-self' : ''}`}
      title={`${SEAT_LABELS[player.seat]} ${WIND_NAMES[player.wind] ?? ''}${
        player.isDealer ? ' · 亲' : ''
      }`}
    >
      <span className="avatar-inner">
        <span className="avatar-wind">{WIND_NAMES[player.wind] ?? '?'}</span>
        {player.isDealer && <span className="avatar-dealer">亲</span>}
      </span>
    </div>
  );
});

// ════════════════════════════════════════════════════════════════
// 中央信息
// ════════════════════════════════════════════════════════════════

const CenterInfo = memo(function CenterInfo({
  info,
  panels,
}: {
  info: TableCenterInfo;
  panels: ReactNode;
}) {
  return (
    <div className="center-info">
      {panels}
      <div className="center-core">
      <div className="center-round">
        <span className="round-wind">{WIND_NAMES[info.roundWind]}</span>
        <span className="round-num">{info.roundNumber}局</span>
        {info.honba > 0 && <span className="round-honba">{info.honba}本场</span>}
      </div>

      <div className="center-dora">
        <span className="dora-label">宝</span>
        <div className="dora-tiles">
          {info.doraIndicators.map((idx, i) => (
            <Tile key={i} index={idx} scale="xs" />
          ))}
        </div>
      </div>

      <div className="center-remaining">
        <span className="remaining-num">{info.tilesRemaining}</span>
        <span className="remaining-label">枚残</span>
      </div>

      {info.riichiSticks > 0 && (
        <div className="center-sticks">
          <span className="sticks-num">{info.riichiSticks}</span>
          <span className="sticks-label">供托</span>
        </div>
      )}
      </div>
    </div>
  );
});

// ════════════════════════════════════════════════════════════════
// 主牌桌
// ════════════════════════════════════════════════════════════════

export interface TableProps {
  players: readonly PlayerView[];
  center: TableCenterInfo;
  selfSeat: number;
  onClickTile?: (pos: number, tileIndex: number) => void;
  /** 当前选中的手牌位置（两下出牌：第一下选中）*/
  selectedHandPos?: number | null;
  /** 是否显示提示（可打牌的推荐）*/
  showHints?: boolean;
  hintIndices?: readonly number[];
  /** 自家可执行动作按钮（自摸/立直/杠，仅可操作时由外层传入），渲染在自家手牌上一行右侧空白处 */
  selfActions?: ReactNode;
}

/** 竖屏（窄而高）：左右两家舍牌河用更多每行张数，避免横向撑爆中间列 */
function usePortrait(): boolean {
  const [portrait, setPortrait] = useState(
    () => typeof window !== 'undefined' && window.innerHeight > window.innerWidth,
  );
  useEffect(() => {
    const onResize = () => setPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return portrait;
}

/**
 * 四家牌桌
 *
 * 座位映射（以自家为基准）：
 *   self = 0（下方）、right = 1（右侧）、across = 2（上方）、left = 3（左侧）
 */
export const Table = memo(function Table({
  players,
  center,
  selfSeat,
  onClickTile,
  selectedHandPos = null,
  showHints = false,
  hintIndices = [],
  selfActions,
}: TableProps) {
  const hintSet = new Set(hintIndices);
  const bySeat = (offset: number) => players[(selfSeat + offset) % 4];
  // 竖屏时左右两家的河用更多列（竖条变高、横向变窄）
  const sideRiverCols = usePortrait() ? RIVER_COLS_SIDE_PORTRAIT : RIVER_COLS;

  const self = bySeat(0);
  const right = bySeat(1);
  const across = bySeat(2);
  const left = bySeat(3);

  // 他家手牌显示为牌背
  const backHandCount = (p: PlayerView) => {
    const meldTiles = p.melds.reduce((s, m) => s + (m.kind === 'kantsu' ? 4 : 3), 0);
    return Math.max(0, 13 - meldTiles);
  };

  return (
    <div className="table">
      {/* 对家（上）：手牌贴顶边，信息牌在内侧（靠中央）*/}
      <div className="table-row table-top">
        <div className="hand-row">
          {Array.from({ length: backHandCount(across) }).map((_, i) => (
            <Tile key={i} faceDown scale="xs" />
          ))}
        </div>
        <MeldDisplay melds={across.melds} scale="xs" rotate={180} />
      </div>

      {/* 中间行：左家手牌 | 中央牌河盘（正方形 + 四家舍牌河围边）| 右家手牌 */}
      <div className="table-row table-middle">
        <div className="table-side table-left">
          <div className="hand-col">
            {Array.from({ length: backHandCount(left) }).map((_, i) => (
              <Tile key={i} faceDown scale="xs" orientation="rotate-right" />
            ))}
          </div>
          <MeldDisplay melds={left.melds} scale="xs" vertical rotate={90} />
        </div>

        {/* 中央牌河盘：正方形中央 + 四家舍牌河贴其对应边 */}
        <div className="center-board">
          {/* 对家舍牌河（贴上方边）*/}
          <div className="board-slot board-across">
            <DiscardRiver
              discards={across.discards}
              discardsRed={across.discardsRed}
              riichiIndex={across.riichiDiscardIndex}
              scale="xs"
              rotate={180}
            />
          </div>
          {/* 上家舍牌河（贴左边，竖排，牌朝上家）*/}
          <div className="board-slot board-left-river">
            <DiscardRiver
              discards={left.discards}
              discardsRed={left.discardsRed}
              riichiIndex={left.riichiDiscardIndex}
              scale="xs"
              rotate={90}
              cols={sideRiverCols}
            />
          </div>
          {/* 中央正方形信息区 */}
          <CenterInfo
            info={center}
            panels={
              <>
                <CenterPanel
                  player={across}
                  isSelf={false}
                  isActive={center.activeSeat === across.seat}
                  edge="top"
                />
                <CenterPanel
                  player={left}
                  isSelf={false}
                  isActive={center.activeSeat === left.seat}
                  edge="left"
                />
                <CenterPanel
                  player={right}
                  isSelf={false}
                  isActive={center.activeSeat === right.seat}
                  edge="right"
                />
                <CenterPanel
                  player={self}
                  isSelf
                  isActive={center.activeSeat === self.seat}
                  edge="bottom"
                />
                {/* 四角头像：自家=左下、上家=左上、对家=右上、下家=右下 */}
                <CenterAvatar
                  player={self}
                  isSelf
                  isActive={center.activeSeat === self.seat}
                  corner="bl"
                />
                <CenterAvatar
                  player={left}
                  isSelf={false}
                  isActive={center.activeSeat === left.seat}
                  corner="tl"
                />
                <CenterAvatar
                  player={across}
                  isSelf={false}
                  isActive={center.activeSeat === across.seat}
                  corner="tr"
                />
                <CenterAvatar
                  player={right}
                  isSelf={false}
                  isActive={center.activeSeat === right.seat}
                  corner="br"
                />
              </>
            }
          />
          {/* 下家舍牌河（贴右边，竖排，牌朝下家）*/}
          <div className="board-slot board-right-river">
            <DiscardRiver
              discards={right.discards}
              discardsRed={right.discardsRed}
              riichiIndex={right.riichiDiscardIndex}
              scale="xs"
              rotate={270}
              cols={sideRiverCols}
            />
          </div>
          {/* 自家舍牌河（贴下方边）*/}
          <div className="board-slot board-self">
            <DiscardRiver
              discards={self.discards}
              discardsRed={self.discardsRed}
              riichiIndex={self.riichiDiscardIndex}
              scale="xs"
            />
          </div>
        </div>

        <div className="table-side table-right">
          <div className="hand-col">
            {Array.from({ length: backHandCount(right) }).map((_, i) => (
              <Tile key={i} faceDown scale="xs" orientation="rotate-right" />
            ))}
          </div>
          <MeldDisplay melds={right.melds} scale="xs" vertical rotate={270} />
        </div>
      </div>

      {/* 自家（下）*/}
      <div className="table-row table-bottom">
        {/* 手牌上一行：左＝副露，右＝自家动作按钮（自摸/立直/杠，可操作时才出现） */}
        <div className="self-top-row">
          <MeldDisplay melds={self.melds} scale="sm" />
          <div className="self-actions">{selfActions}</div>
        </div>
        <div className="hand-row hand-self">
          {self.hand.map((idx, i) => (
            <Tile
              key={i}
              index={idx}
              isRed={self.handRed[i] ?? false}
              scale="md"
              // ⚠️ 摸到的牌用 drawnIndex（id 追踪）高亮，不靠位置拆分
              isDrawn={self.drawnIndex === i}
              clickable={!!onClickTile}
              selected={selectedHandPos === i}
              highlighted={showHints && hintSet.has(idx)}
              onClick={() => onClickTile?.(i, idx)}
            />
          ))}
        </div>
      </div>
    </div>
  );
});
